import {
    validateAlphaNumericString,
    validateAlphaNumericStringAllowingOnlyNumbers,
    validateAlphaNumericStringLenient,
    validateNumber,
    validateNumericString,
    validateSingleSelection,
    validateTime,
} from '@om/validation';
import { setIncidentDetailsFieldValid } from '../../../actions/allSureMotorClaim/incidentDetails';
import { getIncidentDetailsForm } from '../../../selectors/allSureMotorClaim/incidentDetails';
import { INCIDENT_DETAIL_LABELS, RADIO_OPTION } from '../../../actions/allSureMotorClaim/types';
import { validateEmailAddress } from '@om/validation/src/validators/email-validator';
import { validateDate, validateDateBW, ValidateYear } from '@om/validation/src/validators/date-validator';

let prevValidationAction = null;

export const incidentDetailsValidations = (store, next, action) => {
    const state = store.getState();
    const {
        personToBeKeptInformed,
        dateOfIncident,
        creditAgreement,
        thirdParty,
        vehicleMake,
        vehicleModel,
        driverLicense,
        dateOfAccident,
        visibilityAtTheTimeOfIncident,
    } = getIncidentDetailsForm(state);
    const removeWhiteSpacesFromValue = (value) => (value ? value.replace(/ /g, '') : value);
    const trimValue = (value) => (value ? value.trim() : value);
    const getTomorrowDate = () => {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate());
        const day = currentDate.getDate();
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const getLessThanThreeYears = () => {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate());
        const day = currentDate.getDate();
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear() - 3;
        return `${day}/${month}/${year}`;
    };

    const todayDate = dateOfIncident?.value && dateOfIncident?.value;
    const todayDateAccident = dateOfAccident?.value && dateOfAccident?.value;

    const calculateDaysFromDate = (incidentDate) => {
        const [day, month, year] = incidentDate.split('/').map(Number);
        const incident = new Date(year, month - 1, day);
        const currentDate = new Date();
        const timeDiff = currentDate - incident;
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        return days;
    };

    const validationAction = (() => {
        let error;
        const fieldType = action.fieldType || action.value?.fieldType;

        const isWitnessName = /^witnessName_\d+$/;
        const isWitnessPhone = /^witnessPhone_\d+$/;
        const isWitnessAddress = /^witnessAddress_\d+$/;
        const isPassengerName = /^passengerName_\d+$/;
        const isPassengerInjury = /^passengerInjury_\d+$/;
        const isPassengerAddress = /^passengerAddress_\d+$/;

        if (isWitnessName.test(fieldType)) {
            error = validateAlphaNumericStringLenient(action.value.value, {
                min: 2,
                max: 50,
                required: false,
                subject: 'witness name',
            });
        } else if (isWitnessPhone.test(fieldType)) {
            error = validateNumericString(action.value.value, {
                min: 6,
                max: 8,
                required: false,
                subject: 'witness telephone number',
            });
        } else if (isWitnessAddress.test(fieldType)) {
            error = validateAlphaNumericStringLenient(action.value.value, {
                min: 2,
                max: 100,
                required: false,
                subject: 'witness address',
            });
        } else if (isPassengerName.test(fieldType)) {
            error = validateAlphaNumericStringLenient(action.value.value, {
                min: 2,
                max: 50,
                required: false,
                subject: 'passenger name',
            });
        } else if (isPassengerInjury.test(fieldType)) {
            error = validateAlphaNumericStringLenient(action.value.value, {
                min: 2,
                max: 250,
                required: false,
                subject: 'passenger injury',
            });
        } else if (isPassengerAddress.test(fieldType)) {
            error = validateAlphaNumericStringLenient(action.value.value, {
                min: 2,
                max: 100,
                required: false,
                subject: 'passenger address',
            });
        } else {
            switch (action.value.fieldType) {
                case 'vehicleMake':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'mentionedVehicleMake':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        subject: 'vehicle make',
                        required: true,
                    });
                    break;
                case 'vehicleModel':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'mentionedVehicleModel':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        subject: 'vehicle model',
                        required: true,
                    });
                    break;
                case 'registrationNumber':
                    error = validateAlphaNumericStringAllowingOnlyNumbers(action.value.value, {
                        subject: 'registration number',
                        min: 2,
                        max: 50,
                    });
                    break;
                case 'vehicleYear':
                    error = validateNumber(action.value.value, {
                        subject: 'vehicle year',
                        min: 1900,
                        max: new Date().getFullYear(),
                    });
                    break;
                case 'creditAgreement':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'nameOfFinance':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        required: true,
                        subject: 'name of finance company',
                    });
                    break;
                case 'addressOfFinance':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 100,
                        required: true,
                        subject: 'physical address of finance company.',
                    });
                    break;
                case 'nameOfVehicleRegistration':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        required: true,
                        subject: 'vehicle registered owner’s name',
                    });
                    break;
                case 'dateOfIncident':
                    error = validateDateBW(action.value.value, {
                        min: getLessThanThreeYears(),
                        max: getTomorrowDate(),
                        selectable: true,
                        subject: 'date of incident',
                        required: true,
                    });
                    break;
                case 'reasonForDelay':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 25,
                        max: 2500,
                        required: true,
                        subject: 'reason for late notification',
                    });
                    break;
                case 'timeOfIncident':
                    error = validateTime(action.value.value, {
                        date: todayDate,
                        subject: 'time of incident',
                        required: true,
                    });
                    break;
                case 'addressOrLocation':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 100,
                        required: true,
                        subject: 'address or location of incident',
                    });
                    break;
                case 'windScreenType':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'causeOfBreakage':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 500,
                        required: true,
                        subject: 'cause of breakage',
                    });
                    break;
                case 'personResponsible':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        required: false,
                        subject: 'person responsible name',
                    });
                    break;
                case 'witness':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'driverName':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        required: true,
                        subject: 'driver’s name',
                    });
                    break;
                case 'driverLicense':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'driverLicenseNumber':
                    error = validateAlphaNumericStringAllowingOnlyNumbers(action.value.value, {
                        min: 2,
                        max: 50,
                        required: true,
                        subject: 'driver’s licence number',
                    });
                    break;
                case 'driverLicenseCode':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'thirdParty':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'thirdPartyName':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 100,
                        required: false,
                        subject: 'third party name',
                    });
                    break;
                case 'thirdPartyNumber':
                    error = validateNumericString(removeWhiteSpacesFromValue(action.value.value), {
                        min: 5,
                        max: 16,
                        required: false,
                        subject: 'third party cellphone number',
                    });
                    break;
                case 'thirdPartyAddress':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 100,
                        required: false,
                        subject: 'third party address',
                    });
                    break;
                case 'thirdPartyEmail':
                    error = validateEmailAddress(action.value.value, {
                        min: 7,
                        max: 60,
                        required: false,
                        subject: 'third party email address',
                    });
                    break;

                case 'personToBeKeptInformed':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'personsFullName':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        required:true,
                        subject: 'contact person’s full name',
                    });
                    break;
                case 'personsContact':
                    error = validateNumericString(removeWhiteSpacesFromValue(action.value.value), {
                        min: 5,
                        max: 16,
                        subject: 'contact person’s cellphone number',
                        required: true,
                    });
                    break;
                case 'personsEmail':
                    error = validateEmailAddress(trimValue(action.value.value), {
                        min: 7,
                        max: 60,
                        subject: 'contact person’s email address',
                        required: false,
                    });
                    break;
                case 'mileage':
                    error = validateNumber(removeWhiteSpacesFromValue(action.value.value), {
                        min: 1,
                        subject: 'mileage (in km)',
                        required: true,
                    });
                    break;
                case 'vehicleIdNumber':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        subject: 'vehicle identification number',
                        required: false,
                    });
                    break;
                case 'chassisNumber':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        subject: 'chassis number',
                        required: true,
                    });
                    break;
                case 'engineNumber':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        subject: 'engine number',
                        required: true,
                    });
                    break;
                case 'exteriorColor':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 20,
                        subject: 'exterior colour',
                        required: true,
                    });
                    break;
                case 'interiorColor':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 20,
                        subject: 'interior colour',
                        required: true,
                    });
                    break;
                case 'grossVehicleMass':
                    error = validateNumber(removeWhiteSpacesFromValue(action.value.value), {
                        min: 500,
                        subject: 'gross vehicle mass (in kg)',
                        required: true,
                    });
                    break;
                case 'tare':
                    error = validateNumber(removeWhiteSpacesFromValue(action.value.value), {
                        min: 500,
                        subject: 'tare/unladen weight (in kg)',
                        required: true,
                    });
                    break;
                case 'vehicleValue':
                    error = validateNumber(removeWhiteSpacesFromValue(action.value.value), {
                        min: 1,
                        subject: 'vehicle value (in P)',
                        required: true,
                    });
                    break;
                case 'dateOfPurchase':
                    error = validateDateBW(action.value.value, {
                        min: '01/01/1900',
                        max: getTomorrowDate(),
                        selectable: true,
                        subject: 'date of purchase',
                        required: true,
                    });
                    break;
                case 'kiloMeterCompleted':
                    error = validateNumber(removeWhiteSpacesFromValue(action.value.value), {
                        min: 1,
                        subject: 'kilometers completed/mileage',
                        required: true,
                    });
                    break;

                case 'dateOfAccident':
                    error = validateDateBW(action.value.value, {
                        min: getLessThanThreeYears(),
                        max: getTomorrowDate(),
                        selectable: true,
                        subject: 'date of incident',
                        required: true,
                    });
                    break;
                case 'reasonForDelayAccident':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 25,
                        max: 2500,
                        required:true,
                        subject: 'reason for late notification',
                    });
                    break;
                case 'timeOfIncidentAccident':
                    error = validateTime(action.value.value, {
                        date: todayDateAccident,
                        subject: 'time of incident',
                        required: true,
                    });
                    break;
                case 'addressOrLocationAccident':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 100,
                        required: true,
                        subject: 'address or location of incident',
                    });
                    break;
                case 'speedBeforeImpact':
                    error = validateNumber(removeWhiteSpacesFromValue(action.value.value), {
                        min: 0,
                        max: 320,
                        subject: 'speed before impact (in km/h)',
                        required: true,
                    });
                    break;
                case 'speedAfterImpact':
                    error = validateNumber(removeWhiteSpacesFromValue(action.value.value), {
                        min: 0,
                        max: 320,
                        subject: 'speed at the moment of impact (in km/h)',
                        required: true,
                    });
                    break;
                case 'weatherCondition':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'visibilityAtTheTimeOfIncident':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'otherVisibility':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 50,
                        subject: 'other visibility conditions',
                        required: visibilityAtTheTimeOfIncident?.value === 'Other',
                    });
                    break;
                case 'roadSurfaceType':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'widthOfRoad':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'vehicleLightIncident':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'streetLight':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'warning':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'anyPassenger':
                    error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'passenger':
                    error = action.value.value ? validateSingleSelection(action.value.value, 'option') : null;
                    break;
                // case 'employee':
                //     error = validateSingleSelection(action.value.value, 'option');
                    break;
                case 'purpose':
                    error = validateAlphaNumericStringLenient(action.value.value, {
                        min: 2,
                        max: 100,
                        required: false,
                        subject: 'purpose',
                    });
                    break;
                default:
                    return null;
            }
        }

        return setIncidentDetailsFieldValid(error, action.value.fieldType);
    })();

    if (!validationAction) {
        return next(action);
    }

    store.dispatch(validationAction);
    prevValidationAction = validationAction;
    return next(action);
};
